import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  ViewList as ListViewIcon,
  ViewModule as KanbanViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  hitlService,
  TaskStatus,
  TaskType,
  type ExternalTaskDto,
  type ExternalTaskFilters,
} from '../services/hitlService';
import { useNotificationStore } from '../store/notificationStore';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

const statusColor = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.Pending:
      return 'warning' as const;
    case TaskStatus.InProgress:
      return 'info' as const;
    case TaskStatus.Approved:
    case TaskStatus.Completed:
      return 'success' as const;
    case TaskStatus.Rejected:
      return 'error' as const;
    case TaskStatus.Cancelled:
      return 'default' as const;
    default:
      return 'default' as const;
  }
};

const taskTypeColor = (taskType: TaskType) => {
  switch (taskType) {
    case TaskType.Approval:
      return 'primary' as const;
    case TaskType.Review:
      return 'info' as const;
    case TaskType.Action:
      return 'warning' as const;
    case TaskType.Information:
      return 'default' as const;
    default:
      return 'default' as const;
  }
};

type ViewMode = 'list' | 'kanban';

/**
 * Task list page component.
 */
export const TaskListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [tasks, setTasks] = useState<ExternalTaskDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const [filters, setFilters] = useState<ExternalTaskFilters>({
    pageNumber: 1,
    pageSize: 20,
    searchTerm: '',
    status: undefined,
    taskType: undefined,
    assignedTo: undefined,
  });

  const loadTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await hitlService.getTasks(filters);
      setTasks(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
      addNotification('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [filters.pageNumber, filters.pageSize, filters.status, filters.taskType, filters.assignedTo, filters.searchTerm]);

  // Define table columns
  const tableColumns: DataTableColumn<ExternalTaskDto>[] = [
    {
      id: 'title',
      label: 'Title',
      minWidth: 250,
      render: (task) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate(`/tasks/${task.id}`)}
          >
            {task.title}
          </Typography>
          {task.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {task.description.substring(0, 100)}
              {task.description.length > 100 ? '...' : ''}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'externalTaskSystemName',
      label: 'System',
      minWidth: 150,
      render: (task) => <Typography variant="body2">{task.externalTaskSystemName}</Typography>,
    },
    {
      id: 'taskType',
      label: 'Type',
      minWidth: 120,
      render: (task) => (
        <Chip label={task.taskType} color={taskTypeColor(task.taskType)} size="small" />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 130,
      render: (task) => <Chip label={task.status} color={statusColor(task.status)} size="small" />,
    },
    {
      id: 'assignedTo',
      label: 'Assigned To',
      minWidth: 150,
      render: (task) => (
        <Typography variant="body2">{task.assignedTo ? `User ${task.assignedTo.substring(0, 8)}...` : 'Unassigned'}</Typography>
      ),
    },
    {
      id: 'dueDate',
      label: 'Due Date',
      minWidth: 150,
      render: (task) =>
        task.dueDate ? (
          <Typography variant="body2">{new Date(task.dueDate).toLocaleDateString()}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No due date
          </Typography>
        ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 150,
      render: (task) => <Typography variant="body2">{new Date(task.createdAt).toLocaleDateString()}</Typography>,
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      align: 'right',
      render: (task) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => navigate(`/tasks/${task.id}`)}>
              <Typography variant="body2" color="primary">
                View
              </Typography>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / (filters.pageSize || 20));

  // Group tasks by status for Kanban view
  const kanbanColumns = [
    { status: TaskStatus.Pending, label: 'Pending' },
    { status: TaskStatus.InProgress, label: 'In Progress' },
    { status: TaskStatus.Approved, label: 'Approved' },
    { status: TaskStatus.Completed, label: 'Completed' },
    { status: TaskStatus.Rejected, label: 'Rejected' },
  ];

  const renderKanbanView = () => {
    return (
      <Grid container spacing={3}>
        {kanbanColumns.map((column) => {
          const columnTasks = tasks.filter((task) => task.status === column.status);
          return (
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={column.status}>
              <Card sx={{ height: '100%', minHeight: 400 }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" fontWeight={600}>
                    {column.label}
                  </Typography>
                  <Chip label={columnTasks.length} size="small" sx={{ mt: 1 }} />
                </Box>
                <Box sx={{ p: 2, maxHeight: 600, overflowY: 'auto' }}>
                  {columnTasks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No tasks
                    </Typography>
                  ) : (
                    columnTasks.map((task) => (
                      <Card
                        key={task.id}
                        sx={{
                          mb: 2,
                          p: 2,
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 3 },
                        }}
                        onClick={() => navigate(`/tasks/${task.id}`)}
                      >
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                          {task.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          <Chip label={task.taskType} size="small" color={taskTypeColor(task.taskType)} />
                          <Typography variant="caption" color="text.secondary">
                            {task.externalTaskSystemName}
                          </Typography>
                        </Box>
                        {task.dueDate && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Due: {new Date(task.dueDate).toLocaleDateString()}
                          </Typography>
                        )}
                      </Card>
                    ))
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <PageListLayout
      title="Tasks"
      createButton={{ label: 'Create Task', onClick: () => navigate('/tasks/new'), icon: <AddIcon /> }}
      headerActions={
        <>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="list">
              <ListViewIcon />
            </ToggleButton>
            <ToggleButton value="kanban">
              <KanbanViewIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTasks} disabled={loading}>
            Refresh
          </Button>
        </>
      }
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      loading={loading}
      isEmpty={tasks.length === 0}
      emptyState={{
        title: 'No tasks found',
        description: 'Create a new task to get started.',
        action: { label: 'Create Task', onClick: () => navigate('/tasks/new') },
      }}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Search tasks"
            size="small"
            value={filters.searchTerm || ''}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 })}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || ''}
              label="Status"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value ? (e.target.value as TaskStatus) : undefined,
                  pageNumber: 1,
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              {Object.values(TaskStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Task Type</InputLabel>
            <Select
              value={filters.taskType || ''}
              label="Task Type"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  taskType: e.target.value ? (e.target.value as TaskType) : undefined,
                  pageNumber: 1,
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              {Object.values(TaskType).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      }
      pagination={{
        page: filters.pageNumber || 1,
        totalPages,
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      {viewMode === 'list' ? (
        <DataTable
          columns={tableColumns}
          rows={tasks}
        />
      ) : (
        renderKanbanView()
      )}
    </PageListLayout>
  );
};
